/** Native drag-and-drop helpers shared by the calendar views. */

import type { CSSProperties } from "react";

import type { Task } from "@app/core";

import { splitVirtualId } from "../../lib/virtual-id.js";

export { splitVirtualId } from "../../lib/virtual-id.js";

export const TASK_MIME = "application/x-app-task-id";

// Whether a task block is currently being dragged (grabbed & moved). Used to
// suppress the hover-to-expand animation while a drag is in flight.
let dragging = false;
export function setTaskDragging(v: boolean): void { dragging = v; }
export function isTaskDragging(): boolean { return dragging; }

// --- Live "organic" block drag ------------------------------------------------
// When a timed block is grabbed we record HOW FAR DOWN it was grabbed (minutes
// from its top) plus its duration, so the drop can keep the grabbed point under
// the cursor (rather than snapping the block's start to the cursor) and the
// hovered column can render a live ghost of the block that tracks the cursor.

/** How the grabbed block looks, captured at dragstart so the hovered column's
 *  live ghost can render as the real block (wash colors / tone class / title). */
export interface BlockDragVisual {
  task: Task;
  /** Wash CSS vars from `washStyle` (empty when `washed` is false). */
  vars: CSSProperties;
  washed: boolean;
  /** Fallback priority tone class when not washed. */
  toneClass: string;
}

interface BlockDragState { grabOffsetMin: number; durationMin: number; visual: BlockDragVisual }
let blockDrag: BlockDragState | null = null;

/** Cached 1×1 transparent image used to suppress the browser's default drag
 *  ghost so only our live preview shows. Created lazily (needs the DOM). */
let transparentImg: HTMLImageElement | null = null;
function getTransparentImg(): HTMLImageElement {
  if (!transparentImg) {
    transparentImg = new Image();
    transparentImg.src =
      "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
  }
  return transparentImg;
}

export function beginBlockDrag(e: React.DragEvent, grabOffsetMin: number, durationMin: number, visual: BlockDragVisual): void {
  blockDrag = { grabOffsetMin, durationMin, visual };
  try { e.dataTransfer?.setDragImage(getTransparentImg(), 0, 0); } catch { /* unsupported */ }
}
export function endBlockDrag(): void { blockDrag = null; }
export function blockDragState(): BlockDragState | null { return blockDrag; }

export function setDragTaskId(e: DragEvent | React.DragEvent, id: string): void {
  e.dataTransfer?.setData(TASK_MIME, id);
  e.dataTransfer?.setData("text/plain", id);
  if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
}

export function getDragTaskId(e: React.DragEvent): string | null {
  return e.dataTransfer.getData(TASK_MIME) || e.dataTransfer.getData("text/plain") || null;
}

/**
 * The original occurrence ISO a (possibly virtual) task represents. For a
 * virtual occurrence return the iso encoded in its id so reschedules target the
 * dragged occurrence, not the series' base/dtstart; otherwise its `due_at`.
 */
export function occurrenceIsoOf(task: { id: string; due_at: string | null }): string | null {
  return splitVirtualId(task.id).occIso ?? task.due_at;
}

/**
 * Replace the date portion of an ISO due_at, preserving time-of-day (or apply
 * a specific hour/minute). Returns ISO UTC.
 */
export function rescheduleTo(prevIso: string | null, target: Date, hour?: number, minute?: number): string {
  const out = new Date(target);
  if (hour !== undefined) {
    out.setHours(hour, minute ?? 0, 0, 0);
  } else if (prevIso) {
    const prev = new Date(prevIso);
    out.setHours(prev.getHours(), prev.getMinutes(), 0, 0);
  } else {
    out.setHours(9, 0, 0, 0);
  }
  return out.toISOString();
}

export function isSameYMD(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}
